import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { ZoomControl } from '@/components/ui/zoom-control'
import {
  Globe,
  DollarSign,
  Printer,
  Save,
  UtensilsCrossed,
  Check,
  Loader2,
  Upload,
  Trash2,
  Plus,
  ArrowUp,
  ArrowDown,
  Image as ImageIcon,
  Link2,
  FileText,
  Shield,
  ChevronDown,
  Palette,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { OnlineSubMethod } from '@/types'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { DEFAULT_DISPLAY_CURRENCY, parseCurrencyFromSettings, setDisplayCurrency } from '@/lib/currency'
import { resizeImageToDataUrl } from '@/lib/imageUpload'
import { ReceiptPreview } from '@/components/admin/ReceiptPreview'
import { FiscalSettingsPanel } from '@/components/admin/FiscalSettingsPanel'
import { AdminPublicLinksSettings } from '@/components/admin/AdminPublicLinksSettings'
import { AdminStorefrontAppearance } from '@/components/admin/AdminStorefrontAppearance'
import { AdminStorefrontHours } from '@/components/admin/AdminStorefrontHours'
import { AdminStorefrontFeatured } from '@/components/admin/AdminStorefrontFeatured'
import {
  OnlineOrderingGateSkeleton,
  OnlineOrderingPaywall,
} from '@/components/admin/OnlineOrderingPaywall'
import { useOnlineOrderingEnabled } from '@/hooks/useOnlineOrderingEnabled'
import { useModuleVisibility } from '@/hooks/useModuleVisibility'
import { parseReceiptSettings, type ReceiptCustomField } from '@/lib/printCustomerReceipt'
import { ReceiptPrinterSetting } from '@/components/admin/ReceiptPrinterSetting'
import { BridgePrintSetting } from '@/components/admin/BridgePrintSetting'
import {
  normalizeEnabledOrderTypes,
  generateCustomOrderTypeId,
  isBuiltinOrderTypeId,
  sanitizeOrderTypeHexColor,
  type OrderTypeConfig,
} from '@/lib/orderTypeSettings'

type SettingsSection =
  | 'general'
  | 'public-links'
  | 'storefront-appearance'
  | 'financial'
  | 'receipt'
  | 'fiscal'
  | 'order-types'

const NAV_ITEMS: { id: SettingsSection; label: string; icon: typeof Globe; description: string }[] = [
  { id: 'general', label: 'General', icon: Globe, description: 'Business name and currency' },
  { id: 'public-links', label: 'Public links', icon: Link2, description: 'Customer-facing URL used by every QR code' },
  { id: 'storefront-appearance', label: 'Online Ordering', icon: Palette, description: 'Storefront branding — colours, logo, banner, theme' },
  { id: 'financial', label: 'Financial', icon: DollarSign, description: 'Tax rates and POS fee' },
  { id: 'receipt', label: 'Receipt & Printing', icon: Printer, description: 'Receipt branding and layout' },
  { id: 'fiscal', label: 'Tax & Fiscal', icon: Shield, description: 'Tax invoices and FBR/PRA reporting' },
  { id: 'order-types', label: 'Order Types', icon: UtensilsCrossed, description: 'Manage available order types' },
  // Marketing QR moved out — now lives at /admin/marketing/qr-codes
  // alongside Promo codes / Loyalty / Campaigns. Keeps the marketing
  // surfaces in one place and removes the lone non-config tab from
  // Settings.
]

function isValidSettingsSection(s: string): s is SettingsSection {
  return (NAV_ITEMS as readonly { id: SettingsSection }[]).some((i) => i.id === s)
}

export interface AdminSettingsProps {
  /** From `/admin/settings?section=…` (validated in route). */
  initialUrlSection?: string
}

export function AdminSettings({ initialUrlSection }: AdminSettingsProps = {}) {
  const location = useLocation()
  const navigate = useNavigate()
  const [activeSection, setActiveSection] = useState<SettingsSection>(() =>
    initialUrlSection && isValidSettingsSection(initialUrlSection)
      ? initialUrlSection
      : 'general',
  )

  useEffect(() => {
    if (initialUrlSection && isValidSettingsSection(initialUrlSection)) {
      setActiveSection(initialUrlSection)
    }
  }, [initialUrlSection])

  const setSettingsSection = useCallback(
    (id: SettingsSection) => {
      setActiveSection(id)
      if (location.pathname === '/admin/settings') {
        navigate({ to: '/admin/settings', search: { section: id }, replace: true })
      }
    },
    [location.pathname, navigate],
  )

  const queryClient = useQueryClient()
  const { toast } = useToast()

  const getSettingsErrorMessage = (operation: string, error: unknown) => {
    const raw = error instanceof Error ? error.message : String(error)
    if (raw.toLowerCase().includes('insufficient_permissions')) {
      return `${operation} failed due to role permissions (${raw}).`
    }
    return `${operation} failed: ${raw}`
  }

  // ── Global settings query ──
  const { data: allSettingsRes } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
  })

  // Premium gate for the Online Ordering settings tab (storefront branding /
  // hours / featured). Locked tenants see the compact paywall instead.
  const onlineOrderingGate = useOnlineOrderingEnabled()

  // Tabs that only configure a hidden module (ENABLED_MODULES) drop out:
  // storefront branding belongs to online ordering, and the public base URL
  // only feeds customer-facing QR codes (storefront + marketing).
  const modules = useModuleVisibility()
  const isSectionShown = (id: SettingsSection): boolean => {
    if (id === 'storefront-appearance') return modules.isEnabled('online-orders')
    if (id === 'public-links') {
      return modules.isEnabled('online-orders') || modules.isEnabled('marketing')
    }
    return true
  }
  const navItems = NAV_ITEMS.filter((item) => isSectionShown(item.id))
  const shownSection: SettingsSection = isSectionShown(activeSection) ? activeSection : 'general'

  // ── General ──
  const [restaurantName, setRestaurantName] = useState('')
  const [currency, setCurrency] = useState(DEFAULT_DISPLAY_CURRENCY)
  const [dayBoundaryHour, setDayBoundaryHour] = useState(5)
  const [closingsPerDay, setClosingsPerDay] = useState(1)
  // F-CTRL-N32. Kept as a string so the field can be emptied while typing —
  // Number('') is 0, which would silently rewrite the float to zero mid-edit.
  const [defaultOpeningFloat, setDefaultOpeningFloat] = useState('0')

  useEffect(() => {
    const d = allSettingsRes?.data as Record<string, unknown> | undefined
    if (!d) return
    if (typeof d.restaurant_name === 'string') setRestaurantName(d.restaurant_name)
    setCurrency(parseCurrencyFromSettings(d.currency) ?? DEFAULT_DISPLAY_CURRENCY)
    if (typeof d.day_boundary_hour === 'number') setDayBoundaryHour(d.day_boundary_hour)
    if (typeof d.closings_per_day === 'number') setClosingsPerDay(d.closings_per_day)
    if (typeof d.default_opening_float === 'number') {
      setDefaultOpeningFloat(String(d.default_opening_float))
    }
  }, [allSettingsRes])

  const saveGeneralMutation = useMutation({
    mutationFn: async () => {
      await apiClient.updateSetting('restaurant_name', restaurantName)
      await apiClient.updateSetting('currency', currency)
      await apiClient.updateSetting('day_boundary_hour', dayBoundaryHour)
      await apiClient.updateSetting('closings_per_day', closingsPerDay)
      // Blank or unparseable means "no float" — 0, never a carried-over guess.
      const parsedFloat = Number(defaultOpeningFloat)
      await apiClient.updateSetting(
        'default_opening_float',
        Number.isFinite(parsedFloat) && parsedFloat >= 0 ? parsedFloat : 0,
      )
    },
    onSuccess: () => {
      setDisplayCurrency(currency)
      // Invalidate the whole 'settings' tree, not just ['settings','all'].
      // OpenDayDialog reads ['settings','default_opening_float'] with a 5-minute
      // staleTime, so a narrower invalidation would leave "Start the day"
      // pre-filling the OLD float for minutes after it was changed here.
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      toast({ title: 'Settings saved', description: 'General settings updated.' })
    },
    onError: (e: unknown) => {
      toast({
        title: 'Save failed',
        description: getSettingsErrorMessage('General settings save', e),
        variant: 'destructive',
      })
    },
  })

  // ── Financial ──
  const [checkoutRates, setCheckoutRates] = useState({
    tax_cash_pct: '15',
    tax_card_pct: '5',
    tax_online_pct: '15',
  })

  // F-MM-N9 — FBR PKR 1 POS service fee. Off by default everywhere.
  const [posFee, setPosFee] = useState({ enabled: false, amount: '1.00' })

  // The backend gate is authoritative (pricing.resolvePosServiceFee); this is
  // only so the screen doesn't imply a PRA tenant will be charged. Chargeable
  // authorities are FBR / PRAL_POS / LOCAL_BRIDGE — anything else, including
  // an unconfigured authority, charges nothing.
  const posFeeBlockedByAuthority = useMemo(() => {
    const d = allSettingsRes?.data as Record<string, unknown> | undefined
    const cfg = d?.['fiscal_tax_config'] as { authority?: string } | undefined
    const authority = String(cfg?.authority ?? '').trim().toUpperCase()
    if (!authority) return false // unconfigured — don't cry wolf
    return !['FBR', 'PRAL_POS', 'LOCAL_BRIDGE'].includes(authority)
  }, [allSettingsRes])

  useEffect(() => {
    const d = allSettingsRes?.data as Record<string, unknown> | undefined
    if (!d) return
    const num = (k: string, fallback: string) => {
      const v = d[k]
      if (typeof v === 'number') return String(Math.round(v * 10000) / 100)
      return fallback
    }
    setCheckoutRates({
      tax_cash_pct: num('tax_rate_cash', '15'),
      tax_card_pct: num('tax_rate_card', '5'),
      tax_online_pct: num('tax_rate_online', '15'),
    })
    // F-MM-N9: a flat rupee amount, NOT a percentage — read it raw rather
    // than through num(), which rescales fractions to percent.
    const rawFee = d['pos_service_fee_amount']
    setPosFee({
      enabled: d['pos_service_fee_enabled'] === true,
      amount: typeof rawFee === 'number' ? String(rawFee) : '1.00',
    })
  }, [allSettingsRes])

  // Whether the till refuses to ring a stock product with nothing left.
  // Off by default: a shop that doesn't keep perfect counts would otherwise be
  // blocked from selling stock it can physically see.
  const [blockOversell, setBlockOversell] = useState(false)
  useEffect(() => {
    const d = allSettingsRes?.data as Record<string, unknown> | undefined
    if (!d) return
    setBlockOversell(d['inventory.block_oversell'] === true || d['inventory.block_oversell'] === 'true')
  }, [allSettingsRes])

  const saveInventoryPolicy = useMutation({
    mutationFn: (next: boolean) => apiClient.updateSetting('inventory.block_oversell', next),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'all'] })
      toast({ title: 'Settings saved', description: 'Inventory policy updated.' })
    },
    onError: (e: unknown) => {
      toast({
        title: 'Save failed',
        description: getSettingsErrorMessage('Inventory policy save', e),
        variant: 'destructive',
      })
    },
  })

  const saveCheckoutRates = useMutation({
    mutationFn: async () => {
      const toFrac = (s: string) => parseFloat(s) / 100
      await apiClient.updateSetting('tax_rate_cash', toFrac(checkoutRates.tax_cash_pct))
      await apiClient.updateSetting('tax_rate_card', toFrac(checkoutRates.tax_card_pct))
      await apiClient.updateSetting('tax_rate_online', toFrac(checkoutRates.tax_online_pct))
      // F-MM-N9 — flat rupees, so no toFrac. A non-numeric or negative entry
      // falls back to 1.00 rather than writing junk into a money setting.
      const feeAmount = parseFloat(posFee.amount)
      await apiClient.updateSetting('pos_service_fee_enabled', posFee.enabled)
      await apiClient.updateSetting(
        'pos_service_fee_amount',
        Number.isFinite(feeAmount) && feeAmount > 0 ? feeAmount : 1.0,
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'all'] })
      queryClient.invalidateQueries({ queryKey: ['counterPricing'] })
      toast({ title: 'Settings saved', description: 'Tax and service rates updated.' })
    },
    onError: (e: unknown) => {
      toast({
        title: 'Save failed',
        description: getSettingsErrorMessage('Financial settings save', e),
        variant: 'destructive',
      })
    },
  })

  // ── Online payment sub-methods (migration 050) ──
  const { data: onlineSubRes } = useQuery({
    queryKey: ['payment-submethods'],
    queryFn: () =>
      apiClient.getOnlineSubMethods().then((r) => (r.success && r.data ? r.data : null)),
  })
  const [onlineSubEnabled, setOnlineSubEnabled] = useState(false)
  const [onlineSubList, setOnlineSubList] = useState<OnlineSubMethod[]>([])
  useEffect(() => {
    if (!onlineSubRes) return
    setOnlineSubEnabled(!!onlineSubRes.enabled)
    setOnlineSubList((onlineSubRes.methods ?? []).map((m) => ({ ...m })))
  }, [onlineSubRes])

  const addOnlineSub = () =>
    setOnlineSubList((l) => [...l, { slug: '', label: '', is_active: true, sort_order: l.length }])
  const updateOnlineSubLabel = (i: number, label: string) =>
    setOnlineSubList((l) => l.map((m, idx) => (idx === i ? { ...m, label } : m)))
  const toggleOnlineSubActive = (i: number, v: boolean) =>
    setOnlineSubList((l) => l.map((m, idx) => (idx === i ? { ...m, is_active: v } : m)))
  const removeOnlineSub = (i: number) =>
    setOnlineSubList((l) => l.filter((_, idx) => idx !== i))

  const saveOnlineSubMethods = useMutation({
    mutationFn: async () => {
      // Drop empty-label rows client-side; the backend slugifies labels,
      // enforces unique slugs, and rejects "enabled with no active method".
      const methods = onlineSubList
        .map((m, idx) => ({ ...m, label: m.label.trim(), sort_order: idx }))
        .filter((m) => m.label !== '')
      const res = await apiClient.updateOnlineSubMethods({ enabled: onlineSubEnabled, methods })
      if (!res.success) throw new Error(res.error || res.message || 'save_failed')
      return res.data
    },
    onSuccess: (data) => {
      if (data) {
        setOnlineSubEnabled(!!data.enabled)
        setOnlineSubList((data.methods ?? []).map((m) => ({ ...m })))
      }
      queryClient.invalidateQueries({ queryKey: ['payment-submethods'] })
      queryClient.invalidateQueries({ queryKey: ['counterPricing'] })
      toast({ title: 'Saved', description: 'Online payment methods updated.' })
    },
    onError: (e: unknown) => {
      toast({
        title: 'Save failed',
        description: getSettingsErrorMessage('Online payment methods save', e),
        variant: 'destructive',
      })
    },
  })

  // ── Receipt ──
  type ReceiptFormState = {
    business_name: string
    address: string
    ntn: string
    pos_number: string
    logo_url: string
    logo_width_percent: string
    phone: string
    email: string
    website: string
    accent_color: string
    thank_you: string
    /** Footer/policy line printed on the customer-facing refund receipt. */
    refund_footer_message: string
    custom_fields: ReceiptCustomField[]
    // Paper & typography — stored as strings so the Select components can
    // round-trip cleanly; coerced to numbers at save time.
    paper_width_mm: string
    printable_area_mm: string
    font_scale: string
    kot_font_scale: string
  }

  const EMPTY_RECEIPT_FORM: ReceiptFormState = {
    business_name: '',
    address: '',
    ntn: '',
    pos_number: '',
    logo_url: '',
    logo_width_percent: '75',
    phone: '',
    email: '',
    website: '',
    accent_color: '#111827',
    thank_you: 'Thank you for your visit!',
    refund_footer_message: 'Refunds are processed to your original payment method.',
    custom_fields: [],
    paper_width_mm: '80',
    printable_area_mm: '72',
    font_scale: '1.00',
    kot_font_scale: '1.15',
  }

  const [customerReceipt, setCustomerReceipt] = useState<ReceiptFormState>(EMPTY_RECEIPT_FORM)

  // ── Tax invoice (printed PRA-style slip) — see also Tax & Fiscal for digital sync ──
  // These live in the same `app_settings` key-value store under simple keys,
  // gated by `pra_invoice_enabled`. When disabled, the post-payment prompt
  // is never shown to cashiers.
  type PraFormState = {
    enabled: boolean
    qr_url_template: string
    footer_note: string
    /** Allow cashiers to reprint a PRA invoice after the order was completed. */
    late_print_enabled: boolean
    /**
     * Number of full days after the order's completion (or creation, if the
     * order has no completed_at) during which a non-admin user may still issue
     * a reprint. 0 = same business day only. Capped at 7 by the backend.
     */
    late_print_window_days: string
  }
  const EMPTY_PRA_FORM: PraFormState = {
    enabled: false,
    qr_url_template: '',
    footer_note: '',
    late_print_enabled: true,
    late_print_window_days: '7',
  }
  const [praForm, setPraForm] = useState<PraFormState>(EMPTY_PRA_FORM)
  // Controls the "Advanced" disclosure inside the printed-slip card.
  const [slipAdvancedOpen, setSlipAdvancedOpen] = useState(false)

  // ── Master tax gate ──
  // A single on/off that organizes the Tax & Fiscal page: when off, all the tax
  // invoicing + government-reporting config stays collapsed behind one switch.
  // This is a UI/intent gate ONLY — it does not change checkout math. The tax
  // *rate* that bills the customer lives in Financial, and the printed slip
  // keeps its own `pra_invoice_enabled` flag as the source of truth at the
  // counter. Persisted as the `tax_enabled` app-setting (KV store, no schema).
  const [taxEnabled, setTaxEnabled] = useState(false)
  const taxEnabledInitedRef = useRef(false)
  useEffect(() => {
    const d = allSettingsRes?.data as Record<string, unknown> | undefined
    if (!d || taxEnabledInitedRef.current) return
    taxEnabledInitedRef.current = true
    if (typeof d.tax_enabled === 'boolean') {
      setTaxEnabled(d.tax_enabled)
      return
    }
    // No explicit choice saved yet: infer ON for tenants who already collect tax
    // or already print the slip, so nothing they configured disappears on first
    // load. Brand-new tenants (all zero/off) start OFF for a clean setup.
    const rate = (k: string) => (typeof d[k] === 'number' ? (d[k] as number) : 0)
    const chargesTax =
      rate('tax_rate_cash') > 0 || rate('tax_rate_card') > 0 || rate('tax_rate_online') > 0
    setTaxEnabled(chargesTax || d.pra_invoice_enabled === true)
  }, [allSettingsRes])

  const saveTaxEnabled = useMutation({
    mutationFn: (v: boolean) => apiClient.updateSetting('tax_enabled', v),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings', 'all'] }),
    onError: (e: unknown) =>
      toast({
        title: 'Save failed',
        description: getSettingsErrorMessage('Tax setup toggle', e),
        variant: 'destructive',
      }),
  })
  const handleToggleTaxEnabled = (v: boolean) => {
    setTaxEnabled(v)
    saveTaxEnabled.mutate(v)
  }

  useEffect(() => {
    const d = allSettingsRes?.data as Record<string, unknown> | undefined
    if (!d) return
    const str = (k: string) => (typeof d[k] === 'string' ? (d[k] as string) : '')
    const parsedCustom = Array.isArray(d.receipt_custom_fields)
      ? (d.receipt_custom_fields as unknown[])
          .map((r, idx): ReceiptCustomField | null => {
            if (!r || typeof r !== 'object') return null
            const rec = r as Record<string, unknown>
            const label = typeof rec.label === 'string' ? rec.label : ''
            const value = typeof rec.value === 'string' ? rec.value : ''
            if (!label && !value) return null
            return {
              id: typeof rec.id === 'string' && rec.id ? rec.id : `cf-${Date.now()}-${idx}`,
              label,
              value,
              position: rec.position === 'header' ? 'header' : 'footer',
              style: rec.style === 'bold' || rec.style === 'muted' ? rec.style : 'normal',
            }
          })
          .filter((v): v is ReceiptCustomField => v !== null)
      : []
    setCustomerReceipt({
      business_name: str('receipt_business_name') || (typeof d.restaurant_name === 'string' ? d.restaurant_name : ''),
      address: str('receipt_address'),
      ntn: str('receipt_ntn'),
      pos_number: str('receipt_pos_number'),
      logo_url: str('receipt_logo_url'),
      logo_width_percent:
        typeof d.receipt_logo_width_percent === 'number'
          ? String(Math.round(d.receipt_logo_width_percent))
          : str('receipt_logo_width_percent') || '75',
      phone: str('receipt_phone'),
      email: str('receipt_email'),
      website: str('receipt_website'),
      accent_color: /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(str('receipt_accent_color'))
        ? str('receipt_accent_color')
        : '#111827',
      thank_you: str('receipt_thank_you') || 'Thank you for your visit!',
      refund_footer_message:
        str('receipt_refund_footer_message') ||
        'Refunds are processed to your original payment method.',
      custom_fields: parsedCustom,
      paper_width_mm:
        typeof d.receipt_paper_width_mm === 'number'
          ? String(Math.round(d.receipt_paper_width_mm))
          : str('receipt_paper_width_mm') || '80',
      printable_area_mm:
        typeof d.receipt_printable_area_mm === 'number'
          ? String(Math.round(d.receipt_printable_area_mm))
          : str('receipt_printable_area_mm') || '72',
      font_scale:
        typeof d.receipt_font_scale === 'number'
          ? d.receipt_font_scale.toFixed(2)
          : str('receipt_font_scale') || '1.00',
      kot_font_scale:
        typeof d.kot_font_scale === 'number'
          ? d.kot_font_scale.toFixed(2)
          : str('kot_font_scale') || '1.15',
    })
    const lateEnabledRaw = d.pra_invoice_late_print_enabled
    const windowDaysRaw = d.pra_invoice_late_print_window_days
    setPraForm({
      enabled: d.pra_invoice_enabled === true,
      qr_url_template: str('pra_invoice_qr_url_template'),
      footer_note: str('pra_invoice_footer_note'),
      late_print_enabled: lateEnabledRaw === undefined ? true : lateEnabledRaw === true,
      late_print_window_days:
        typeof windowDaysRaw === 'number'
          ? String(Math.max(0, Math.min(7, Math.round(windowDaysRaw))))
          : '7',
    })
  }, [allSettingsRes])

  const saveCustomerReceipt = useMutation({
    mutationFn: async () => {
      const logoWidth = Math.min(80, Math.max(70, Number(customerReceipt.logo_width_percent || '75') || 75))
      await apiClient.updateSetting('receipt_business_name', customerReceipt.business_name)
      await apiClient.updateSetting('receipt_address', customerReceipt.address)
      await apiClient.updateSetting('receipt_ntn', customerReceipt.ntn)
      await apiClient.updateSetting('receipt_pos_number', customerReceipt.pos_number)
      await apiClient.updateSetting('receipt_logo_url', customerReceipt.logo_url)
      await apiClient.updateSetting('receipt_logo_width_percent', logoWidth)
      await apiClient.updateSetting('receipt_phone', customerReceipt.phone)
      await apiClient.updateSetting('receipt_email', customerReceipt.email)
      await apiClient.updateSetting('receipt_website', customerReceipt.website)
      await apiClient.updateSetting('receipt_accent_color', customerReceipt.accent_color)
      await apiClient.updateSetting('receipt_thank_you', customerReceipt.thank_you)
      await apiClient.updateSetting(
        'receipt_refund_footer_message',
        customerReceipt.refund_footer_message,
      )
      await apiClient.updateSetting(
        'receipt_custom_fields',
        customerReceipt.custom_fields.map((f) => ({
          id: f.id,
          label: f.label,
          value: f.value,
          position: f.position,
          style: f.style ?? 'normal',
        })),
      )
      // Paper & typography — clamped here too so older browsers can't sneak
      // out-of-range values into app_settings.
      const paperWidth = Math.max(58, Math.min(112, Number(customerReceipt.paper_width_mm) || 80))
      const printable = Math.max(48, Math.min(110, Number(customerReceipt.printable_area_mm) || 72))
      const fontScale = Math.max(0.85, Math.min(1.6, Number(customerReceipt.font_scale) || 1.0))
      const kotScale = Math.max(0.85, Math.min(1.6, Number(customerReceipt.kot_font_scale) || 1.15))
      await apiClient.updateSetting('receipt_paper_width_mm', paperWidth)
      await apiClient.updateSetting('receipt_printable_area_mm', printable)
      await apiClient.updateSetting('receipt_font_scale', fontScale)
      await apiClient.updateSetting('kot_font_scale', kotScale)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'all'] })
      toast({ title: 'Settings saved', description: 'Receipt branding updated.' })
    },
    onError: (e: unknown) => {
      toast({
        title: 'Save failed',
        description: getSettingsErrorMessage('Receipt settings save', e),
        variant: 'destructive',
      })
    },
  })

  const savePraInvoice = useMutation({
    mutationFn: async () => {
      const days = Math.max(
        0,
        Math.min(7, Math.round(Number(praForm.late_print_window_days || '7') || 0)),
      )
      await apiClient.updateSetting('pra_invoice_enabled', praForm.enabled)
      await apiClient.updateSetting('pra_invoice_qr_url_template', praForm.qr_url_template.trim())
      await apiClient.updateSetting('pra_invoice_footer_note', praForm.footer_note.trim())
      await apiClient.updateSetting('pra_invoice_late_print_enabled', praForm.late_print_enabled)
      await apiClient.updateSetting('pra_invoice_late_print_window_days', days)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'all'] })
      toast({ title: 'Saved', description: 'Tax invoice settings updated.' })
    },
    onError: (e: unknown) => {
      toast({
        title: 'Save failed',
        description: getSettingsErrorMessage('Tax invoice settings save', e),
        variant: 'destructive',
      })
    },
  })

  // Custom fields helpers
  const addCustomField = (position: 'header' | 'footer') => {
    setCustomerReceipt((prev) => ({
      ...prev,
      custom_fields: [
        ...prev.custom_fields,
        { id: `cf-${Date.now()}`, label: '', value: '', position, style: 'normal' },
      ],
    }))
  }
  const updateCustomField = (id: string, patch: Partial<ReceiptCustomField>) => {
    setCustomerReceipt((prev) => ({
      ...prev,
      custom_fields: prev.custom_fields.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    }))
  }
  const removeCustomField = (id: string) => {
    setCustomerReceipt((prev) => ({
      ...prev,
      custom_fields: prev.custom_fields.filter((f) => f.id !== id),
    }))
  }
  const moveCustomField = (id: string, dir: -1 | 1) => {
    setCustomerReceipt((prev) => {
      const idx = prev.custom_fields.findIndex((f) => f.id === id)
      if (idx < 0) return prev
      const target = idx + dir
      if (target < 0 || target >= prev.custom_fields.length) return prev
      const next = [...prev.custom_fields]
      ;[next[idx], next[target]] = [next[target], next[idx]]
      return { ...prev, custom_fields: next }
    })
  }

  // Logo uploader — downscale client-side to keep app_settings payload small.
  const logoFileInputRef = useRef<HTMLInputElement | null>(null)
  const handleLogoFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please choose an image file (PNG, JPG, SVG).', variant: 'destructive' })
      return
    }
    try {
      const dataUrl = await resizeImageToDataUrl(file, 400, 0.92)
      setCustomerReceipt((p) => ({ ...p, logo_url: dataUrl }))
      toast({ title: 'Logo loaded', description: 'Preview updated. Click Save to persist.' })
    } catch (e) {
      toast({ title: 'Upload failed', description: e instanceof Error ? e.message : String(e), variant: 'destructive' })
    }
  }

  // Derive a live CustomerReceiptSettings object for the preview that also
  // includes the current tax/service rates the admin is editing in-session.
  const previewSettings = useMemo(() => {
    const raw = (allSettingsRes?.data as Record<string, unknown> | undefined) ?? {}
    const toFrac = (s: string) => {
      const n = parseFloat(s)
      return Number.isFinite(n) ? n / 100 : 0
    }
    const merged: Record<string, unknown> = {
      ...raw,
      receipt_business_name: customerReceipt.business_name,
      receipt_address: customerReceipt.address,
      receipt_ntn: customerReceipt.ntn,
      receipt_pos_number: customerReceipt.pos_number,
      receipt_logo_url: customerReceipt.logo_url,
      receipt_logo_width_percent:
        Math.min(80, Math.max(70, Number(customerReceipt.logo_width_percent || '75') || 75)),
      receipt_phone: customerReceipt.phone,
      receipt_email: customerReceipt.email,
      receipt_website: customerReceipt.website,
      receipt_accent_color: customerReceipt.accent_color,
      receipt_thank_you: customerReceipt.thank_you,
      receipt_refund_footer_message: customerReceipt.refund_footer_message,
      receipt_custom_fields: customerReceipt.custom_fields,
      receipt_paper_width_mm: Number(customerReceipt.paper_width_mm) || 80,
      receipt_printable_area_mm: Number(customerReceipt.printable_area_mm) || 72,
      receipt_font_scale: Number(customerReceipt.font_scale) || 1.0,
      kot_font_scale: Number(customerReceipt.kot_font_scale) || 1.15,
      tax_rate_cash: toFrac(checkoutRates.tax_cash_pct),
      tax_rate_card: toFrac(checkoutRates.tax_card_pct),
      tax_rate_online: toFrac(checkoutRates.tax_online_pct),
    }
    return parseReceiptSettings(merged)
  }, [allSettingsRes, customerReceipt, checkoutRates])

  // ── Order Types ── (built-ins + custom rows in app_settings.enabled_order_types)
  const { data: orderTypes = [] } = useQuery<OrderTypeConfig[]>({
    queryKey: ['settings', 'enabled_order_types'],
    queryFn: async () => {
      const res = await apiClient.getSetting('enabled_order_types')
      return res.success && res.data ? res.data : []
    },
  })

  const [localOrderTypes, setLocalOrderTypes] = useState<OrderTypeConfig[]>(() =>
    normalizeEnabledOrderTypes([])
  )

  useEffect(() => {
    setLocalOrderTypes(normalizeEnabledOrderTypes(orderTypes))
  }, [orderTypes])

  const saveOrderTypesMutation = useMutation({
    mutationFn: async (types: OrderTypeConfig[]) => apiClient.updateSetting('enabled_order_types', types),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings', 'enabled_order_types'] }),
    onError: (e: unknown) => {
      toast({
        title: 'Save failed',
        description: getSettingsErrorMessage('Order types save', e),
        variant: 'destructive',
      })
    },
  })

  // Server (table-service) interface sidebar visibility. Absent/true → shown;
  // explicit false → hidden. Invalidates the shared all-settings key so the
  // sidebar (AdminSidebar) reacts live without a reload.
  const [serverInterfaceEnabled, setServerInterfaceEnabled] = useState(true)
  useEffect(() => {
    const d = allSettingsRes?.data as Record<string, unknown> | undefined
    setServerInterfaceEnabled(d?.['ui.server_interface_enabled'] !== false)
  }, [allSettingsRes])

  const saveServerInterfaceMutation = useMutation({
    mutationFn: (v: boolean) => apiClient.updateSetting('ui.server_interface_enabled', v),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings', 'all'] }),
    onError: (e: unknown) =>
      toast({
        title: 'Save failed',
        description: getSettingsErrorMessage('Server interface toggle', e),
        variant: 'destructive',
      }),
  })

  const [orderTypeModalId, setOrderTypeModalId] = useState<string | null>(null)
  const [otModalEnabled, setOtModalEnabled] = useState(true)
  const [otModalDeliveryFee, setOtModalDeliveryFee] = useState('0')
  const [otModalLabel, setOtModalLabel] = useState('')
  const [otModalColor, setOtModalColor] = useState('#64748b')

  const [addOrderTypeOpen, setAddOrderTypeOpen] = useState(false)
  const [addOtLabel, setAddOtLabel] = useState('')
  const [addOtColor, setAddOtColor] = useState('#64748b')

  const openOrderTypeModal = (id: string) => {
    const t = localOrderTypes.find((x) => x.id === id)
    if (!t) return
    setOrderTypeModalId(id)
    setOtModalEnabled(t.enabled)
    setOtModalDeliveryFee(String(t.delivery_fee ?? 0))
    setOtModalLabel(t.label || t.id)
    const fallback = sanitizeOrderTypeHexColor(t.color, '#64748b')
    setOtModalColor(fallback)
  }

  const saveOrderTypeModal = () => {
    if (!orderTypeModalId) return
    const df =
      orderTypeModalId === 'delivery'
        ? Math.max(0, Number.parseFloat(otModalDeliveryFee.replace(/,/g, '')) || 0)
        : 0
    const labelTrim = otModalLabel.trim() || orderTypeModalId
    const prev = localOrderTypes.find((x) => x.id === orderTypeModalId)
    const baseFallback = prev?.color ? sanitizeOrderTypeHexColor(prev.color, '#64748b') : '#64748b'
    const colorSan = sanitizeOrderTypeHexColor(otModalColor, baseFallback)
    const updated = localOrderTypes.map((t) =>
      t.id === orderTypeModalId
        ? {
            ...t,
            label: labelTrim,
            color: colorSan,
            enabled: otModalEnabled,
            delivery_fee: df,
          }
        : t
    )
    setLocalOrderTypes(updated)
    saveOrderTypesMutation.mutate(updated)
    setOrderTypeModalId(null)
  }

  const deleteCustomOrderType = (id: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (isBuiltinOrderTypeId(id)) return
    const updated = localOrderTypes.filter((t) => t.id !== id)
    setLocalOrderTypes(updated)
    saveOrderTypesMutation.mutate(updated)
  }

  const saveNewOrderType = () => {
    const label = addOtLabel.trim()
    if (!label) {
      toast({
        title: 'Label required',
        description: 'Enter a name for this order type.',
        variant: 'destructive',
      })
      return
    }
    const maxSort = localOrderTypes.reduce((m, t) => Math.max(m, t.sort_order ?? 0), 0)
    const row: OrderTypeConfig = {
      id: generateCustomOrderTypeId(),
      label,
      enabled: true,
      delivery_fee: 0,
      color: sanitizeOrderTypeHexColor(addOtColor, '#64748b'),
      builtin: false,
      sort_order: maxSort + 1,
    }
    const updated = [...localOrderTypes, row]
    setLocalOrderTypes(updated)
    saveOrderTypesMutation.mutate(updated)
    setAddOrderTypeOpen(false)
    setAddOtLabel('')
    setAddOtColor('#64748b')
  }

  // ── Section renderers ──

  const renderPublicLinks = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Public links"
        description="The customer-facing URL used by every QR code your restaurant prints."
      />
      <AdminPublicLinksSettings />
    </div>
  )

  const renderStorefrontAppearance = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Online Ordering"
        description="Brand your customer-facing storefront, set business hours, and feature your best items."
      />
      {onlineOrderingGate.isLoading ? (
        <OnlineOrderingGateSkeleton />
      ) : !onlineOrderingGate.enabled ? (
        <OnlineOrderingPaywall variant="compact" />
      ) : (
        <>
          <AdminStorefrontAppearance />
          <AdminStorefrontHours />
          <AdminStorefrontFeatured />
        </>
      )}
    </div>
  )

  const renderGeneral = () => (
    <div className="space-y-6">
      <SectionHeader
        title="General"
        description="Core restaurant identity and regional preferences"
      />
      <Card>
        <CardContent className="pt-6 space-y-5">
          <FieldGroup label="Restaurant Name" hint="Displayed across the POS system and receipts.">
            <Input
              value={restaurantName}
              onChange={(e) => setRestaurantName(e.target.value)}
              placeholder="Enter restaurant name"
            />
          </FieldGroup>

          <FieldGroup label="Currency" hint="The currency used across menus, orders, and receipts.">
            <select
              className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              <option value="PKR">PKR (Rs.)</option>
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (&euro;)</option>
              <option value="GBP">GBP (&pound;)</option>
            </select>
          </FieldGroup>

          <FieldGroup
            label="Business day starts at"
            hint="Orders placed before this hour count as the previous business day. Set this to a dead hour when your restaurant is never operating (e.g. 5 AM). This controls when the 'Day Close' cutover happens."
          >
            <select
              className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
              value={dayBoundaryHour}
              onChange={(e) => setDayBoundaryHour(Number(e.target.value))}
            >
              <option value={0}>12:00 AM (midnight)</option>
              <option value={1}>1:00 AM</option>
              <option value={2}>2:00 AM</option>
              <option value={3}>3:00 AM</option>
              <option value={4}>4:00 AM</option>
              <option value={5}>5:00 AM (recommended)</option>
              <option value={6}>6:00 AM</option>
              <option value={7}>7:00 AM</option>
              <option value={8}>8:00 AM</option>
            </select>
          </FieldGroup>

          <FieldGroup
            label="Closings per day (shifts)"
            hint="How many times staff close out per day. 1 = a single end-of-day close. 2 = one shift-handover close plus the end-of-day close — each shift counts and reconciles its own drawer. The end-of-day Z report itemizes every shift."
          >
            <select
              className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
              value={closingsPerDay}
              onChange={(e) => setClosingsPerDay(Number(e.target.value))}
            >
              <option value={1}>1 — single end-of-day close</option>
              <option value={2}>2 — one shift close + day close</option>
              <option value={3}>3 — two shift closes + day close</option>
              <option value={4}>4 — three shift closes + day close</option>
            </select>
          </FieldGroup>

          <FieldGroup
            label="Usual starting cash (till float)"
            hint="The cash your restaurant normally leaves in the drawer to start the day. Used to pre-fill 'Start the day' so staff confirm a familiar number instead of retyping it — it is never assumed on its own. Set 0 if you bank the takings every night and start empty. Counter staff can only start the day at this amount; a manager can enter anything."
          >
            <input
              type="text"
              inputMode="decimal"
              className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm tabular-nums"
              placeholder="0"
              value={defaultOpeningFloat}
              onChange={(e) => setDefaultOpeningFloat(e.target.value.replace(/[^0-9.]/g, ''))}
            />
          </FieldGroup>

          <div className="flex justify-end pt-2">
            <Button onClick={() => saveGeneralMutation.mutate()} disabled={saveGeneralMutation.isPending}>
              {saveGeneralMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save General Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Per-device display scale. Not part of saveGeneralMutation — it's saved
          to this device's localStorage instantly (no Save button), because zoom
          belongs to the physical screen, not the restaurant or the operator.
          Lives here as well as the user menu so it's reachable on a tablet where
          the menu dropdown can be awkward to open. */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base">Display size</CardTitle>
          <CardDescription>
            Shrink the interface to fit a smaller screen (e.g. a kitchen tablet). Applies instantly and is
            saved on this device only — it won&apos;t change other stations or follow you to another device.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-w-xs">
            <ZoomControl showLabel={false} />
          </div>
        </CardContent>
      </Card>
    </div>
  )

  const renderFinancial = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Financial"
        description="Tax rates applied during checkout. These values are used across all counter and payment flows"
      />
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base">Inventory</CardTitle>
          <CardDescription>
            Every product is stock-tracked. This decides what the till does when the
            count reaches zero.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <p className="text-sm font-medium">Block selling out-of-stock items</p>
              <p className="text-sm text-muted-foreground">
                On, the counter refuses to add a product with nothing left. Leave it off
                if your counts drift — staff can still sell what they can see, and the
                stock goes negative so you know to correct it.
              </p>
            </div>
            <Switch
              checked={blockOversell}
              onCheckedChange={(next) => {
                setBlockOversell(next)
                saveInventoryPolicy.mutate(next)
              }}
              disabled={saveInventoryPolicy.isPending}
            />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base">Tax Rates</CardTitle>
          <CardDescription>
            Rates are stored as fractions internally. Enter percentages below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FieldGroup label="Cash Tax (%)">
              <Input
                type="number"
                step="0.01"
                value={checkoutRates.tax_cash_pct}
                onChange={(e) => setCheckoutRates({ ...checkoutRates, tax_cash_pct: e.target.value })}
              />
            </FieldGroup>
            <FieldGroup label="Card Tax (%)">
              <Input
                type="number"
                step="0.01"
                value={checkoutRates.tax_card_pct}
                onChange={(e) => setCheckoutRates({ ...checkoutRates, tax_card_pct: e.target.value })}
              />
            </FieldGroup>
            <FieldGroup label="Online Tax (%)">
              <Input
                type="number"
                step="0.01"
                value={checkoutRates.tax_online_pct}
                onChange={(e) => setCheckoutRates({ ...checkoutRates, tax_online_pct: e.target.value })}
              />
            </FieldGroup>
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={() => saveCheckoutRates.mutate()} disabled={saveCheckoutRates.isPending}>
              {saveCheckoutRates.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Financial Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* F-MM-N9 — FBR POS service fee. */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base">FBR POS Service Fee</CardTitle>
          <CardDescription>
            A flat fee charged once per invoice and remitted to FBR. It is added
            after tax and never enters the tax base, so your sales-tax figures are
            unchanged. It is reported as a liability, not revenue.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <div className="text-sm font-medium">Charge the POS service fee</div>
              <div className="text-xs text-muted-foreground">
                Off = customers are charged nothing extra (current behaviour).
              </div>
            </div>
            <Switch
              checked={posFee.enabled}
              onCheckedChange={(v) => setPosFee({ ...posFee, enabled: v })}
            />
          </div>

          <FieldGroup label="Fee per invoice (Rs)">
            <Input
              type="number"
              step="0.01"
              min="0"
              className="max-w-xs"
              value={posFee.amount}
              onChange={(e) => setPosFee({ ...posFee, amount: e.target.value })}
              disabled={!posFee.enabled}
            />
          </FieldGroup>

          {posFeeBlockedByAuthority && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
              This tenant files to <strong>PRA</strong>, which does not levy the FBR
              POS fee. Nothing will be charged even if this is switched on.
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button onClick={() => saveCheckoutRates.mutate()} disabled={saveCheckoutRates.isPending}>
              {saveCheckoutRates.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Financial Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base">Online Payment Methods</CardTitle>
          <CardDescription>
            Split the “Online” tender into providers (Easypaisa, JazzCash, Meezan Bank…).
            All inherit the Online tax rate above — sub-methods never carry their own rate.
            When enabled, the counter requires the cashier to pick a provider for every online payment.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <div className="text-sm font-medium">Enable online sub-methods</div>
              <div className="text-xs text-muted-foreground">
                Off = a single plain “Online” tender (current behaviour).
              </div>
            </div>
            <Switch checked={onlineSubEnabled} onCheckedChange={setOnlineSubEnabled} />
          </div>

          <div className="space-y-2">
            {onlineSubList.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No providers yet. Add Easypaisa, JazzCash, Meezan Bank, etc.
              </p>
            )}
            {onlineSubList.map((m, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  placeholder="Provider name (e.g. Easypaisa)"
                  value={m.label}
                  onChange={(e) => updateOnlineSubLabel(i, e.target.value)}
                />
                <label className="flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
                  <Switch
                    checked={m.is_active}
                    onCheckedChange={(v) => toggleOnlineSubActive(i, v)}
                  />
                  Active
                </label>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeOnlineSub(i)}
                  aria-label="Remove provider"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addOnlineSub}>
              <Plus className="w-4 h-4 mr-1" /> Add provider
            </Button>
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={() => saveOnlineSubMethods.mutate()}
              disabled={saveOnlineSubMethods.isPending}
            >
              {saveOnlineSubMethods.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Online Methods
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )

  const renderReceipt = () => {
    const headerFields = customerReceipt.custom_fields.filter((f) => f.position === 'header')
    const footerFields = customerReceipt.custom_fields.filter((f) => f.position === 'footer')

    return (
      <div className="space-y-6">
        <SectionHeader
          title="Receipt & Printing"
          description="Customize the thermal receipt printed after payment. Invoice number, date, time, payment mode, cashier, and server name are added automatically from each order"
        />

        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
          {/* ── Left column: editable settings ─────────────────────────── */}
          <div className="xl:col-span-3 space-y-6">
            {/* Brand */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-base flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" /> Brand
                </CardTitle>
                <CardDescription>Logo and business name printed at the top of every receipt</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FieldGroup label="Business Name" hint="Printed as the receipt header.">
                  <Input
                    value={customerReceipt.business_name}
                    onChange={(e) => setCustomerReceipt({ ...customerReceipt, business_name: e.target.value })}
                    placeholder="e.g. COVA Cafe"
                  />
                </FieldGroup>

                <FieldGroup label="Logo" hint="Upload an image (auto-resized) or paste a public URL.">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => logoFileInputRef.current?.click()}
                        className="gap-2"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        Upload Image
                      </Button>
                      <input
                        ref={logoFileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) handleLogoFile(file)
                          e.target.value = ''
                        }}
                      />
                      {customerReceipt.logo_url && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setCustomerReceipt({ ...customerReceipt, logo_url: '' })}
                          className="gap-2 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Remove
                        </Button>
                      )}
                    </div>
                    <div className="relative">
                      <Link2 className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={customerReceipt.logo_url.startsWith('data:') ? '' : customerReceipt.logo_url}
                        onChange={(e) => setCustomerReceipt({ ...customerReceipt, logo_url: e.target.value })}
                        placeholder={customerReceipt.logo_url.startsWith('data:')
                          ? 'Using uploaded image (clear to switch to URL)'
                          : 'https://yourcdn.com/logo.png'}
                        className="pl-8"
                        disabled={customerReceipt.logo_url.startsWith('data:')}
                      />
                    </div>
                  </div>
                </FieldGroup>

                <FieldGroup label="Logo Width (%)" hint="Recommended: 70–80.">
                  <Input
                    type="number"
                    min={70}
                    max={80}
                    value={customerReceipt.logo_width_percent}
                    onChange={(e) => setCustomerReceipt({ ...customerReceipt, logo_width_percent: e.target.value })}
                    placeholder="75"
                    className="max-w-[140px]"
                  />
                </FieldGroup>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-base">Contact</CardTitle>
                <CardDescription>Address and reach-out details. All contact lines are optional</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FieldGroup label="Address" hint="Street, area, city (multiple lines OK).">
                  <Textarea
                    rows={3}
                    value={customerReceipt.address}
                    onChange={(e) => setCustomerReceipt({ ...customerReceipt, address: e.target.value })}
                    placeholder={'Street address\nArea, City'}
                    className="resize-y min-h-[72px]"
                  />
                </FieldGroup>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FieldGroup label="Phone">
                    <Input
                      value={customerReceipt.phone}
                      onChange={(e) => setCustomerReceipt({ ...customerReceipt, phone: e.target.value })}
                      placeholder="e.g. +92 300 1234567"
                    />
                  </FieldGroup>
                  <FieldGroup label="Email">
                    <Input
                      type="email"
                      value={customerReceipt.email}
                      onChange={(e) => setCustomerReceipt({ ...customerReceipt, email: e.target.value })}
                      placeholder="hello@yourrestaurant.com"
                    />
                  </FieldGroup>
                </div>
                <FieldGroup label="Website">
                  <Input
                    value={customerReceipt.website}
                    onChange={(e) => setCustomerReceipt({ ...customerReceipt, website: e.target.value })}
                    placeholder="www.yourrestaurant.com"
                  />
                </FieldGroup>
              </CardContent>
            </Card>

            {/* Legal / Tax */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-base">Legal &amp; Tax</CardTitle>
                <CardDescription>Regulatory identifiers printed below the header</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FieldGroup label="NTN / STRN">
                    <Input
                      value={customerReceipt.ntn}
                      onChange={(e) => setCustomerReceipt({ ...customerReceipt, ntn: e.target.value })}
                      placeholder="Tax registration"
                    />
                  </FieldGroup>
                  <FieldGroup label="POS / Counter Number">
                    <Input
                      value={customerReceipt.pos_number}
                      onChange={(e) => setCustomerReceipt({ ...customerReceipt, pos_number: e.target.value })}
                      placeholder="e.g. 176709"
                    />
                  </FieldGroup>
                </div>
              </CardContent>
            </Card>

            {/* Custom lines */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-base">Custom Lines</CardTitle>
                <CardDescription>
                  Add your own labeled lines — useful for tag lines, terms, promotions, or social handles.
                  Choose whether each line appears above the items (Header) or below the totals (Footer).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <CustomFieldsEditor
                  title="Header lines"
                  hint="Shown above the items table, right after NTN/STRN."
                  fields={headerFields}
                  allFields={customerReceipt.custom_fields}
                  onAdd={() => addCustomField('header')}
                  onUpdate={updateCustomField}
                  onRemove={removeCustomField}
                  onMove={moveCustomField}
                />
                <CustomFieldsEditor
                  title="Footer lines"
                  hint="Shown below the Payable row — great for 'Thank you' variants, FBR notes, or policy."
                  fields={footerFields}
                  allFields={customerReceipt.custom_fields}
                  onAdd={() => addCustomField('footer')}
                  onUpdate={updateCustomField}
                  onRemove={removeCustomField}
                  onMove={moveCustomField}
                />
              </CardContent>
            </Card>

            {/* Appearance */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-base">Appearance</CardTitle>
                <CardDescription>Accent color and closing message</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FieldGroup label="Accent Color" hint="Used for section rules, business name, and Payable row.">
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={customerReceipt.accent_color}
                        onChange={(e) => setCustomerReceipt({ ...customerReceipt, accent_color: e.target.value })}
                        className="w-10 h-10 rounded border border-input cursor-pointer bg-transparent"
                        aria-label="Accent color"
                      />
                      <Input
                        value={customerReceipt.accent_color}
                        onChange={(e) => setCustomerReceipt({ ...customerReceipt, accent_color: e.target.value })}
                        placeholder="#111827"
                        className="font-mono uppercase"
                      />
                    </div>
                  </FieldGroup>
                  <FieldGroup label="Thank-You Message" hint="Shown just above the attribution footer.">
                    <Input
                      value={customerReceipt.thank_you}
                      onChange={(e) => setCustomerReceipt({ ...customerReceipt, thank_you: e.target.value })}
                      placeholder="Thank you for your visit!"
                    />
                  </FieldGroup>
                  <FieldGroup
                    label="Refund Receipt Footer"
                    hint="Policy line printed on the customer-facing refund receipt (separate from the internal credit note)."
                  >
                    <Input
                      value={customerReceipt.refund_footer_message}
                      onChange={(e) =>
                        setCustomerReceipt({ ...customerReceipt, refund_footer_message: e.target.value })
                      }
                      placeholder="Refunds are processed to your original payment method."
                    />
                  </FieldGroup>
                </div>
              </CardContent>
            </Card>

            {/* Paper & Type — calibration for the thermal printer */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-base">Paper &amp; Type</CardTitle>
                <CardDescription>
                  Calibrate for your thermal printer. Most "80mm" rolls actually print on 72mm —
                  if the right edge of values clips, lower the printable area.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FieldGroup label="Paper Width" hint="Physical roll width declared to the driver.">
                    <Select
                      value={customerReceipt.paper_width_mm}
                      onValueChange={(v) => setCustomerReceipt({ ...customerReceipt, paper_width_mm: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="58">58 mm</SelectItem>
                        <SelectItem value="72">72 mm</SelectItem>
                        <SelectItem value="76">76 mm</SelectItem>
                        <SelectItem value="80">80 mm</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                  <FieldGroup
                    label="Printable Area"
                    hint="Width used for layout. Lower this if the right edge clips."
                  >
                    <Select
                      value={customerReceipt.printable_area_mm}
                      onValueChange={(v) => setCustomerReceipt({ ...customerReceipt, printable_area_mm: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="48">48 mm (58mm rolls)</SelectItem>
                        <SelectItem value="64">64 mm</SelectItem>
                        <SelectItem value="68">68 mm</SelectItem>
                        <SelectItem value="72">72 mm (recommended)</SelectItem>
                        <SelectItem value="76">76 mm</SelectItem>
                        <SelectItem value="80">80 mm</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                </div>
                <ReceiptPrinterSetting />
                <BridgePrintSetting />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FieldGroup label="Receipt Font Size" hint="Uniform scale for the customer receipt.">
                    <Select
                      value={customerReceipt.font_scale}
                      onValueChange={(v) => setCustomerReceipt({ ...customerReceipt, font_scale: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0.95">Compact (0.95×)</SelectItem>
                        <SelectItem value="1.00">Normal (1.00×)</SelectItem>
                        <SelectItem value="1.15">Large (1.15×)</SelectItem>
                        <SelectItem value="1.30">X-Large (1.30×)</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                  <FieldGroup
                    label="KOT Font Size"
                    hint="Kitchen tickets are read from across the line, so they usually want bigger."
                  >
                    <Select
                      value={customerReceipt.kot_font_scale}
                      onValueChange={(v) => setCustomerReceipt({ ...customerReceipt, kot_font_scale: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0.95">Compact (0.95×)</SelectItem>
                        <SelectItem value="1.00">Normal (1.00×)</SelectItem>
                        <SelectItem value="1.15">Large (1.15×)</SelectItem>
                        <SelectItem value="1.30">X-Large (1.30×)</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button
                size="lg"
                onClick={() => saveCustomerReceipt.mutate()}
                disabled={saveCustomerReceipt.isPending}
              >
                {saveCustomerReceipt.isPending
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Save className="w-4 h-4 mr-2" />}
                Save Receipt Settings
              </Button>
            </div>
          </div>

          {/* ── Right column: live preview ─────────────────────────────── */}
          <div className="xl:col-span-2">
            <Card className="xl:sticky xl:top-6">
              <CardHeader className="pb-4">
                <CardTitle className="text-base">Live Preview</CardTitle>
                <CardDescription>
                  Exact 1:1 preview of the printed receipt with sample items and totals.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ReceiptPreview settings={previewSettings} paymentMethod="cash" />
                <p className="mt-4 text-xs font-medium text-muted-foreground">Provisional bill (pre-payment)</p>
                <ReceiptPreview settings={previewSettings} billKind="unpaid_estimate" />
                <p className="mt-3 text-[11px] text-muted-foreground leading-snug">
                  Preview uses your current tax and service rates from the Financial tab, plus any
                  unsaved edits above. The footer attribution is hardcoded and cannot be removed.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  const renderOrderTypes = () => {
    const modalType = orderTypeModalId
      ? localOrderTypes.find((t) => t.id === orderTypeModalId)
      : undefined
    return (
      <div className="space-y-6">
        <SectionHeader
          title="Order Types"
          description="Built-in types cannot be deleted; add custom types for channels like catering or third-party apps. Colors appear on the checkout counter and dashboard charts."
        />
        <Card>
          <CardContent className="pt-6 space-y-5">
            <div className="flex items-center justify-between gap-3 rounded-md border p-3">
              <div>
                <p className="text-sm font-medium">Server Interface</p>
                <p className="text-xs text-muted-foreground">
                  Show the waiter/tablet ordering screen in the sidebar.
                </p>
              </div>
              <Switch
                checked={serverInterfaceEnabled}
                onCheckedChange={(v) => {
                  setServerInterfaceEnabled(v)
                  saveServerInterfaceMutation.mutate(v)
                }}
                aria-label="Show server interface in sidebar"
              />
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => setAddOrderTypeOpen(true)}>
                <Plus className="w-4 h-4" />
                Add order type
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {localOrderTypes.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => openOrderTypeModal(type.id)}
                  className={`group relative p-4 rounded-lg border-2 text-left transition-all ${
                    type.enabled
                      ? 'border-primary/40 bg-primary/5 hover:border-primary/60'
                      : 'border-muted bg-muted/30 opacity-70 hover:opacity-90'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span
                      className="h-3 w-3 shrink-0 rounded-full border border-border"
                      style={{ backgroundColor: sanitizeOrderTypeHexColor(type.color, '#64748b') }}
                      aria-hidden
                    />
                    <span className="font-medium text-sm">{type.label}</span>
                    <div className="ml-auto flex items-center gap-1 shrink-0">
                      {type.enabled && (
                        <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                          <Check className="w-3 h-3 text-primary-foreground" />
                        </div>
                      )}
                      {!isBuiltinOrderTypeId(type.id) && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
                          title="Remove order type"
                          onClick={(e) => deleteCustomOrderType(type.id, e)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={type.enabled ? 'default' : 'secondary'} className="text-xs">
                      {type.enabled ? 'Active' : 'Disabled'}
                    </Badge>
                    <span className="text-xs text-muted-foreground truncate max-w-[160px]">
                      {type.builtin ? type.id : type.label.toLowerCase().replace(/\s+/g, '_')}
                    </span>
                  </div>
                  {type.id === 'delivery' && (type.delivery_fee ?? 0) > 0 && (
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Delivery fee: {type.delivery_fee}
                    </p>
                  )}
                </button>
              ))}
            </div>

            {saveOrderTypesMutation.isPending && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" />
                Saving...
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog
          open={orderTypeModalId !== null}
          onOpenChange={(o) => {
            if (!o) setOrderTypeModalId(null)
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {modalType ? `Configure ${modalType.label}` : 'Order type'}
              </DialogTitle>
              <DialogDescription>
                Changes apply at the checkout counter for new and updated tickets. Tax rates are set
                under Financial.
              </DialogDescription>
            </DialogHeader>
            {modalType && (
              <div className="space-y-4 py-1">
                <FieldGroup label="Display name">
                  <Input value={otModalLabel} onChange={(e) => setOtModalLabel(e.target.value)} placeholder="e.g. Catering" />
                </FieldGroup>
                <FieldGroup label="Accent color" hint="Shown on counter tabs and reports for this type.">
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={otModalColor.match(/^#[0-9a-fA-F]{6}$/) ? otModalColor : '#64748b'}
                      onChange={(e) => setOtModalColor(e.target.value)}
                      className="w-10 h-10 rounded border border-input cursor-pointer bg-transparent"
                      aria-label="Order type color"
                    />
                    <Input
                      value={otModalColor}
                      onChange={(e) => setOtModalColor(e.target.value)}
                      placeholder="#0d9488"
                      className="font-mono uppercase max-w-[140px]"
                    />
                  </div>
                </FieldGroup>
                <div className="flex items-center justify-between gap-3 rounded-md border p-3">
                  <div>
                    <p className="text-sm font-medium">Available in POS</p>
                    <p className="text-xs text-muted-foreground">When off, staff cannot start this order type.</p>
                  </div>
                  <Switch checked={otModalEnabled} onCheckedChange={setOtModalEnabled} />
                </div>
                {modalType.id === 'delivery' && (
                  <FieldGroup label="Delivery fee (flat per order)">
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={otModalDeliveryFee}
                      onChange={(e) => setOtModalDeliveryFee(e.target.value)}
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Added after tax; not included in the sales tax base. Currency matches your store setting.
                    </p>
                  </FieldGroup>
                )}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOrderTypeModalId(null)}>
                Cancel
              </Button>
              <Button type="button" onClick={saveOrderTypeModal} disabled={saveOrderTypesMutation.isPending}>
                {saveOrderTypesMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={addOrderTypeOpen} onOpenChange={setAddOrderTypeOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add order type</DialogTitle>
              <DialogDescription>
                Creates a new checkout mode alongside dine-in, takeaway, and delivery. Historical orders are unchanged if you remove a type later.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-1">
              <FieldGroup label="Name">
                <Input value={addOtLabel} onChange={(e) => setAddOtLabel(e.target.value)} placeholder="e.g. Uber Eats" />
              </FieldGroup>
              <FieldGroup label="Accent color">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={addOtColor.match(/^#[0-9a-fA-F]{6}$/) ? addOtColor : '#64748b'}
                    onChange={(e) => setAddOtColor(e.target.value)}
                    className="w-10 h-10 rounded border border-input cursor-pointer bg-transparent"
                    aria-label="New order type color"
                  />
                  <Input
                    value={addOtColor}
                    onChange={(e) => setAddOtColor(e.target.value)}
                    placeholder="#64748b"
                    className="font-mono uppercase max-w-[140px]"
                  />
                </div>
              </FieldGroup>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOrderTypeOpen(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={saveNewOrderType} disabled={saveOrderTypesMutation.isPending}>
                {saveOrderTypesMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  const renderFiscal = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Tax &amp; fiscal compliance"
        description="Set up tax invoicing and government reporting for this location."
      />

      {/* Master gate — keeps the page calm until the business actually charges
          tax. UI/intent gate only; the tax rate that bills the customer is set
          under Financial, and the slip keeps its own flag at the counter. */}
      <Card>
        <CardContent className="flex items-start justify-between gap-4 p-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">Do you charge sales tax at this location?</p>
            <p className="text-xs text-muted-foreground mt-1">
              Turn this on to set up tax invoices and FBR/PRA reporting. Your tax{' '}
              <button
                type="button"
                onClick={() => setSettingsSection('financial')}
                className="font-medium text-foreground underline underline-offset-4 hover:text-primary"
              >
                rate is set under Financial
              </button>
              .
            </p>
          </div>
          <Switch
            checked={taxEnabled}
            onCheckedChange={handleToggleTaxEnabled}
            aria-label="Charge sales tax at this location"
          />
        </CardContent>
      </Card>

      {taxEnabled ? (
        <>
          {renderPrintedSlipCard()}
          <FiscalSettingsPanel />
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          Tax invoicing is off. Turn on the switch above once you’re registered to charge
          sales tax, and the setup options will appear here.
        </p>
      )}
    </div>
  )

  // Printed PRA-style tax-invoice slip. Rendered inside the gated Tax & Fiscal
  // page (no page header of its own). The everyday on/off is up top; the QR /
  // footer / reprint-window knobs are tucked under "Advanced" since most
  // businesses never touch them.
  const renderPrintedSlipCard = () => (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="w-4 h-4" /> Printed tax invoice slip
        </CardTitle>
        <CardDescription>
          When on, the cashier is asked after payment whether to print a tax-compliant invoice slip.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-muted/30 p-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">Ask to print a tax invoice at checkout</p>
            <p className="text-xs text-muted-foreground mt-1">
              Adds a post-payment <em>Skip</em> / <em>Print</em> prompt. Leave off to keep
              checkout exactly as it is today.
            </p>
          </div>
          <Switch
            checked={praForm.enabled}
            onCheckedChange={(v) => setPraForm((p) => ({ ...p, enabled: v }))}
            aria-label="Enable tax invoice prompt at checkout"
          />
        </div>

        {praForm.enabled && (
          <div className="border-t pt-4">
            <button
              type="button"
              onClick={() => setSlipAdvancedOpen((o) => !o)}
              className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
              aria-expanded={slipAdvancedOpen}
            >
              <ChevronDown
                className={`w-4 h-4 transition-transform ${slipAdvancedOpen ? '' : '-rotate-90'}`}
              />
              Advanced
            </button>

            {slipAdvancedOpen && (
              <div className="mt-4 space-y-5">
                <FieldGroup
                  label="QR code payload"
                  hint="Optional. Supports {invoice_number} and {order_number}. Leave blank to encode the invoice number directly."
                >
                  <Input
                    value={praForm.qr_url_template}
                    onChange={(e) => setPraForm((p) => ({ ...p, qr_url_template: e.target.value }))}
                    placeholder="https://e.pra.punjab.gov.pk/invoice/{invoice_number}"
                  />
                </FieldGroup>

                <FieldGroup
                  label="Footer note"
                  hint="Optional short line shown on the slip (above the logo area)."
                >
                  <Textarea
                    rows={2}
                    value={praForm.footer_note}
                    onChange={(e) => setPraForm((p) => ({ ...p, footer_note: e.target.value }))}
                    placeholder="e.g. Verify this invoice by scanning the QR code."
                    className="resize-y min-h-[56px]"
                  />
                </FieldGroup>

                <div className="border-t pt-4 space-y-4">
                  <div className="flex items-center justify-between gap-4 rounded-md border border-border bg-muted/20 p-3">
                    <div>
                      <p className="text-sm font-medium">Allow late reprints</p>
                      <p className="text-xs text-muted-foreground">
                        Lets non-admins reprint a slip from <strong>Reports → Daily Sales</strong> for
                        a past order. Admins can always reprint.
                      </p>
                    </div>
                    <Switch
                      checked={praForm.late_print_enabled}
                      onCheckedChange={(v) => setPraForm((p) => ({ ...p, late_print_enabled: v }))}
                      aria-label="Allow late tax invoice reprints"
                    />
                  </div>
                  <FieldGroup
                    label="Reprint window"
                    hint="Days after the order during which a reprint is allowed. 0 = same business day only. Max 7."
                  >
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={7}
                        step={1}
                        value={praForm.late_print_window_days}
                        onChange={(e) =>
                          setPraForm((p) => ({ ...p, late_print_window_days: e.target.value }))
                        }
                        className="w-24"
                        disabled={!praForm.late_print_enabled}
                      />
                      <span className="text-sm text-muted-foreground">days</span>
                    </div>
                  </FieldGroup>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={() => savePraInvoice.mutate()} disabled={savePraInvoice.isPending}>
            {savePraInvoice.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save tax invoice settings
          </Button>
        </div>
      </CardContent>
    </Card>
  )

  const sectionRenderers: Record<SettingsSection, () => JSX.Element> = {
    general: renderGeneral,
    'public-links': renderPublicLinks,
    'storefront-appearance': renderStorefrontAppearance,
    financial: renderFinancial,
    receipt: renderReceipt,
    fiscal: renderFiscal,
    'order-types': renderOrderTypes,
  }

  return (
    <div className="flex flex-col md:flex-row h-full min-h-0 w-full">
      {/* Sidebar navigation */}
      <nav className="hidden md:flex w-56 shrink-0 flex-col border-r border-border bg-muted/30 p-4 overflow-y-auto">
        <h2 className="text-3xl font-bold tracking-tight mt-[9px] mb-[9px] px-2">Settings</h2>
        <p className="text-base text-muted-foreground mb-4 px-2">Configure your store here</p>
        <div className="space-y-0.5">
          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = shownSection === item.id
            return (
              <button
                key={item.id}
                onClick={() => setSettingsSection(item.id)}
                className={`w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-background text-foreground font-medium shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-background/60'
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {item.label}
              </button>
            )
          })}
        </div>
      </nav>

      {/* Mobile nav (horizontal scroll) — in normal flow below the shell header,
          so it can't collide with the app shell's sticky mobile header. */}
      <div className="md:hidden shrink-0 bg-background border-b border-border px-3 py-2 overflow-x-auto no-scrollbar">
        <div className="flex gap-1 min-w-max">
          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = shownSection === item.id
            return (
              <button
                key={item.id}
                onClick={() => setSettingsSection(item.id)}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {item.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Content panel */}
      <main className="flex-1 min-h-0 overflow-y-auto p-6 md:p-8 md:pt-6">
        <div
          className={
            shownSection === 'receipt' ? 'max-w-6xl' : 'max-w-3xl'
          }
        >
          {sectionRenderers[shownSection]()}
        </div>
      </main>
    </div>
  )
}

// ── Shared sub-components ──

function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <div className="mb-2">
      <h2 className="text-3xl font-bold tracking-tight">{title}</h2>
      <p className="text-muted-foreground mt-1">{description}</p>
    </div>
  )
}

function FieldGroup({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="text-sm font-medium mb-1.5 block">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

// ── Custom fields editor ───────────────────────────────────────────────

function CustomFieldsEditor({
  title,
  hint,
  fields,
  allFields,
  onAdd,
  onUpdate,
  onRemove,
  onMove,
}: {
  title: string
  hint: string
  fields: ReceiptCustomField[]
  allFields: ReceiptCustomField[]
  onAdd: () => void
  onUpdate: (id: string, patch: Partial<ReceiptCustomField>) => void
  onRemove: (id: string) => void
  onMove: (id: string, dir: -1 | 1) => void
}) {
  // Position-within-group helpers: we only allow moves within the same group
  // so the preview order stays predictable.
  const indexInGroup = (id: string) => fields.findIndex((f) => f.id === id)
  const indexInAll = (id: string) => allFields.findIndex((f) => f.id === id)

  // Translate an in-group move into an all-fields move (swap with the
  // previous / next field of the same position).
  const moveWithinGroup = (id: string, dir: -1 | 1) => {
    const groupIdx = indexInGroup(id)
    const target = groupIdx + dir
    if (target < 0 || target >= fields.length) return
    const myAllIdx = indexInAll(id)
    const neighborAllIdx = indexInAll(fields[target].id)
    const distance = neighborAllIdx - myAllIdx
    if (distance === 0) return
    const step: -1 | 1 = distance > 0 ? 1 : -1
    for (let i = 0; i < Math.abs(distance); i++) onMove(id, step)
  }

  return (
    <div className="space-y-3">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold">{title}</h4>
          <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onAdd} className="gap-1.5 shrink-0">
          <Plus className="w-3.5 h-3.5" />
          Add line
        </Button>
      </div>

      {fields.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-4 text-xs text-muted-foreground text-center">
          No lines yet. Click <span className="font-medium">Add line</span> to create one.
        </div>
      ) : (
        <div className="space-y-2">
          {fields.map((f, i) => (
            <div
              key={f.id}
              className="rounded-md border border-border bg-background p-3 space-y-2"
            >
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_1.5fr_auto] gap-2">
                <Input
                  value={f.label}
                  onChange={(e) => onUpdate(f.id, { label: e.target.value })}
                  placeholder="Label (optional)"
                  className="h-9"
                />
                <Input
                  value={f.value}
                  onChange={(e) => onUpdate(f.id, { value: e.target.value })}
                  placeholder="Value — e.g. Follow us @covacafe"
                  className="h-9"
                />
                <select
                  value={f.style ?? 'normal'}
                  onChange={(e) =>
                    onUpdate(f.id, {
                      style: e.target.value as 'normal' | 'bold' | 'muted',
                    })
                  }
                  className="h-9 px-2 rounded-md border border-input bg-background text-xs"
                >
                  <option value="normal">Normal</option>
                  <option value="bold">Bold</option>
                  <option value="muted">Muted</option>
                </select>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={i === 0}
                    onClick={() => moveWithinGroup(f.id, -1)}
                    aria-label="Move up"
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={i === fields.length - 1}
                    onClick={() => moveWithinGroup(f.id, 1)}
                    aria-label="Move down"
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </Button>
                  <Badge variant="secondary" className="ml-1 text-[10px] uppercase tracking-wide">
                    {f.position}
                  </Badge>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemove(f.id)}
                  className="h-7 gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Image helpers ──────────────────────────────────────────────────────

/**
 * Read an image file, scale it down to `maxWidth` (preserving aspect ratio),
 * and return a JPEG/PNG data URL. SVGs are returned as-is (data URL), since
 * rasterizing them loses quality.
 */
